import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useGetCurrentUserApi } from "../api/user";
import { rememberUser } from "../state/actions";
import {
  // useGetOrganizationsApi,
  useGetOrganizationInfo,
} from "../api/admin/organization";
import useAuth from "./useAuth";
import { findIntersection } from "../util/Helpers";

export const ROLES = {
  bookkeeper: "bookkeeper",
  hr: "hr",
  finance: "finance",
  employee: "employee",
  manager: "manager",
  admin: "admin",
  super: "super",
};

export const DEFAULT_ORG_ID = "0a98c0a8-dcf5-4fef-abf3-d896ac540e77";

const getUserRoles = (user) => (user?.roles || []).map((role) => role.name);

const useUser = () => {
  const userStored = useSelector((state) => state.redivizUser);
  const dispatch = useDispatch();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const [user, setUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [userOrganization, setUserOrganization] = useState(null);
  // const [firsOrganization, setFirsOrganization] = useState(null);

  const userOrgId = useMemo(() => {
    if (!user) return null;
    return user?.settings?.selected_organization || DEFAULT_ORG_ID;
  }, [user]);

  const {
    data: userData,
    error: userError,
    isInitialLoading: isUserLoading,
    refetch: userRefetch,
  } = useGetCurrentUserApi({
    useQueryOptions: { enabled: false },
  });

  const {
    data: orgsInfoData,
    isInitialLoading: isOrgInfoLoading,
    refetch: userOrgRefetch,
  } = useGetOrganizationInfo(userOrgId, { enabled: isAuthenticated() && !!userOrgId });

  // const { data: orgsData, isInitialLoading: isOrgsLoading } = useGetOrganizationsApi(
  //   { page: 0, limit: 1 },
  //   { enabled: isAuthenticated() && !!user && !userOrgId },
  // );

  const hasRole = useCallback(
    (roles) => {
      const allowedRoles = (Array.isArray(roles) ? roles : [roles]).filter((role) =>
        Object.values(ROLES).includes(role),
      );
      return findIntersection(allowedRoles, userRoles);
    },
    [userRoles],
  );

  useEffect(() => {
    if (!isAuthenticated() || isAuthLoading) {
      return;
    }
    if (isUserLoading) {
      return;
    }
    if (userData && user !== userData) {
      dispatch(rememberUser(userData));
      setUser(userData);
      setUserRoles(getUserRoles(userData));
      return;
    }
    if (!userData) {
      if (!userStored || !Object.keys(userStored).length) {
        userRefetch();
      } else {
        setUser(userStored);
        setUserRoles(getUserRoles(userStored));
      }
    }
  }, [
    isUserLoading,
    isAuthLoading,
    isAuthenticated,
    user,
    userStored,
    userData,
    dispatch,
    userRefetch,
  ]);

  const refetch = useCallback(() => {
    userRefetch();
    !!userOrgId && userOrgRefetch();
  }, [userOrgId, userRefetch, userOrgRefetch]);

  // useEffect(() => {
  //   const org = orgsData?.organizations?.length && orgsData?.organizations[0];
  //   org && setFirsOrganization(org);
  // }, [orgsData]);

  useEffect(() => {
    orgsInfoData && setUserOrganization(orgsInfoData);
  }, [orgsInfoData, setUserOrganization]);

  const isLoading = !!(
    (!isAuthenticated() && isAuthLoading) ||
    !user ||
    (userOrgId && !userOrganization) ||
    isUserLoading ||
    // isOrgsLoading ||
    isOrgInfoLoading
  );

  return {
    user,
    userRoles,
    error: userError,
    isLoading,
    userOrgId,
    userOrganization,
    // firsOrganization,
    hasRole,
    refetch,
  };
};

export default useUser;
